import { Input, InputMask } from "@/components/forms";
import { LocationPickerField, type LocationPickerOption } from "@/components/forms/LocationPickerField";
import { Button, Card, FixedFooter, HiddenField, Text } from "@/components/ui";
import { sameCityCheckEnabled } from "@/consts";
import { AddressSchemaType } from "@/types/forms";
import { FieldErrors, UseFormRegister } from "react-hook-form";

/**
 * Step 1: street/number/district/city/CEP, plus the submit button ("Continuar para o mapa" or, in
 * NEIGHBORHOOD mode, "Salvar endereço" directly). Complement and reference live here only in NEIGHBORHOOD
 * mode; the map flow asks for them after the pin, in `AddressFinishStep`.
 *
 * City and Bairro switch presentation with the store's mode:
 *  - POLYGON: Cidade is free text (locked when `sameCityCheckEnabled`); Bairro is free text.
 *  - NEIGHBORHOOD, city-less: Cidade stays prefilled/editable; Bairro is a closed picker over the flat list.
 *  - NEIGHBORHOOD with cities: BOTH Cidade and Bairro are closed pickers; Bairro is restricted to the chosen city.
 */
export function AddressDetailsForm({
  register,
  errors,
  fields,
  loading,
  cityMismatchBlocks,
  isNeighborhoodMode,
  hasCities,
  cityOptions,
  neighborhoodOptions,
  onSelectCity,
  onSelectNeighborhood,
  onSubmit,
}: {
  register: UseFormRegister<AddressSchemaType>;
  errors: FieldErrors<AddressSchemaType>;
  fields: AddressSchemaType;
  loading: boolean;
  cityMismatchBlocks: boolean;
  isNeighborhoodMode: boolean;
  hasCities: boolean;
  cityOptions: LocationPickerOption[];
  neighborhoodOptions: LocationPickerOption[];
  onSelectCity: (option: LocationPickerOption) => void;
  onSelectNeighborhood: (option: LocationPickerOption) => void;
  onSubmit: () => void;
}) {
  const cityInputDisabled = loading || (sameCityCheckEnabled && !isNeighborhoodMode);
  const neighborhoodPickerDisabled = loading || (hasCities && !fields.city);

  return (
    <>
      <form
        id="addressForm"
        className="flex flex-col gap-8"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Card className="flex flex-col gap-6">
          <Text size="sm" tone="muted">
            Campos com{" "}
            <Text as="span" weight="semibold" tone="primary">
              *
            </Text>{" "}
            são obrigatórios
          </Text>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              {hasCities ? (
                <>
                  <HiddenField {...register("city")} />
                  <LocationPickerField
                    name="city"
                    label="Cidade"
                    disabled={loading}
                    error={!!errors.city}
                    errorMessage="Cidade obrigatória"
                    dialogTitle="Selecione sua cidade"
                    searchPlaceholder="Pesquise pela sua cidade"
                    emptyMessage="Nenhuma cidade encontrada"
                    triggerPlaceholder="Selecione a cidade"
                    selectedName={fields.city}
                    options={cityOptions}
                    onSelect={onSelectCity}
                  />
                </>
              ) : (
                <Input
                  name="city"
                  label="Cidade"
                  error={!!errors.city}
                  errorMsg="Cidade obrigatória"
                  registerAction={register}
                  placeholder="Insira sua cidade"
                  disabled={cityInputDisabled}
                  required
                />
              )}
            </div>
            <InputMask
              name="state"
              label="Estado"
              error={!!errors.state}
              errorMsg="Estado obrigatório"
              registerAction={register}
              mask="__"
              replacement={{ _: /[A-Za-z]/ }}
              placeholder="SP"
              disabled
              required
            />
          </div>

          {isNeighborhoodMode ? (
            <>
              <HiddenField {...register("district")} />
              <LocationPickerField
                name="district"
                label="Bairro"
                disabled={neighborhoodPickerDisabled}
                error={!!errors.district}
                errorMessage="Bairro obrigatório"
                dialogTitle="Selecione seu bairro"
                searchPlaceholder="Pesquise pelo seu bairro"
                emptyMessage={
                  hasCities && !fields.city
                    ? "Selecione a cidade primeiro"
                    : "Nenhum bairro encontrado"
                }
                triggerPlaceholder="Selecione o bairro"
                selectedName={fields.district}
                options={neighborhoodOptions}
                onSelect={onSelectNeighborhood}
              />
            </>
          ) : (
            <Input
              name="district"
              label="Bairro"
              error={!!errors.district}
              errorMsg="Bairro obrigatório"
              registerAction={register}
              placeholder="Insira seu bairro"
              disabled={loading}
              required
            />
          )}

          <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
            <div className="col-span-3 md:col-span-5">
              <Input
                name="street"
                label="Rua"
                error={!!errors.street}
                errorMsg="Rua obrigatória"
                registerAction={register}
                placeholder="Insira sua rua"
                disabled={loading}
                required
              />
            </div>
            <Input
              name="number"
              label="Número"
              type="text"
              error={!!errors.number}
              errorMsg="Número obrigatório"
              registerAction={register}
              placeholder="1234"
              disabled={loading}
              required
            />
          </div>

          {!isNeighborhoodMode && (
            <InputMask
              name="postalCode"
              label="CEP"
              error={!!errors.postalCode}
              errorMsg="CEP inválido"
              registerAction={register}
              mask="_____-___"
              replacement={{ _: /\d/ }}
              placeholder="00000-000"
              disabled={loading}
              required
            />
          )}

          {isNeighborhoodMode && (
            <>
              <Input
                name="complement"
                label="Complemento"
                error={!!errors.complement}
                registerAction={register}
                placeholder="Apto, bloco, casa…"
                disabled={loading}
                required={false}
              />
              <Input
                name="reference"
                label="Referência"
                error={!!errors.reference}
                registerAction={register}
                placeholder="Ponto de referência"
                disabled={loading}
                required={false}
              />
            </>
          )}
        </Card>
      </form>

      <FixedFooter>
        <Button
          type="submit"
          form="addressForm"
          size="lg"
          fullWidth
          disabled={cityMismatchBlocks || loading}
        >
          {isNeighborhoodMode ? "Salvar endereço" : "Continuar para o mapa"}
        </Button>
      </FixedFooter>
    </>
  );
}
